  // MCHD Room Scheduler - Coordinator Dashboard
// Modified: 07-10-2025 14:01:32 - Removed ALL mock data, connected to real APIs
// Purpose: Display real-time coordinator KPIs and management boards

import React from 'react';
import { Container, Paper, Stack, Group, Text, Grid, Card, Badge, Progress, RingProgress, Center, Loader, Alert, Tooltip, ActionIcon, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconInfoCircle, IconHelp, IconActivity } from '@tabler/icons-react';
import {
  useCoordinatorKPIs,
  useRoomStates,
  useUpcomingBlocks,
  useConflicts,
} from '../../hooks/coordinator/useCoordinatorDashboard';

/**
 * Coordinator Dashboard Component
 * 
 * Displays real-time KPIs and management boards for coordinators:
 * - 5 KPIs: Utilization %, Vacant Sessions, Blocked Hours, Conflicts, Published Coverage
 * - Today's room states (assigned, vacant, blocked)
 * - Upcoming blocks (maintenance, cleaning)
 * - Conflict logs with details
 * 
 * NO MOCK DATA - All data fetched from backend APIs
 * 
 * Created: 2025-01-08
 * Modified: 07-10-2025 14:01:32 - Connected to real APIs (Gap G7-1 resolved)
 * Complies: @react-frontend.mdc, @ui-ux-excellence.mdc, @hospital-domain.mdc
 */
const CoordinatorDashboard: React.FC = () => {
  // Fetch real data from APIs using React Query hooks
  const { data: kpis, isLoading: kpisLoading, error: kpisError } = useCoordinatorKPIs();
  const { data: roomStates, isLoading: roomStatesLoading, error: roomStatesError } = useRoomStates();
  const { data: upcomingBlocks, isLoading: blocksLoading, error: blocksError } = useUpcomingBlocks();
  const { data: conflicts, isLoading: conflictsLoading, error: conflictsError } = useConflicts();

  // Combined loading state
  const isLoading = kpisLoading || roomStatesLoading || blocksLoading || conflictsLoading;
  
  // Show loading state
  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <Center style={{ minHeight: '60vh' }}>
          <Stack align="center" gap="md">
            <Loader size="xl" />
            <Text size="lg" c="dimmed">Loading coordinator dashboard...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Show error state for KPIs (most critical data)
  if (kpisError) {
    return (
      <Container size="xl" py="md">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Error Loading Dashboard"
          color="red"
          variant="filled"
        >
          {kpisError.message || 'Failed to load dashboard data. Please try again.'}
        </Alert>
      </Container>
    );
  }

  // Empty state (per UX Touchpoint Spec Gap G7-12)
  if (!kpis || (kpis.utilization === 0 && kpis.vacantSessions === 0 && kpis.conflicts === 0)) {
    return (
      <Container size="xl" py="md">
        <Center style={{ minHeight: '60vh' }}>
          <Stack align="center" gap="md">
            <IconInfoCircle size={48} color="gray" />
            <Text size="xl" fw={600} c="dimmed">No assignments yet. Create schedules in Planner.</Text>
            <Text size="sm" c="dimmed">Dashboard will populate once you start creating room assignments.</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between">
            <Stack gap="xs">
              <Group gap="xs">
                <Text fw={700} size="xl">Coordinator Dashboard</Text>
                <Tooltip label="Real-time monitoring of room scheduling operations, KPIs, and system status">
                  <ActionIcon variant="subtle" color="gray" size="sm">
                    <IconHelp size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Text size="sm" c="dimmed">
                Monitor room utilization, manage assignments, and track system performance
              </Text>
            </Stack>
            <Tooltip label="Data refreshes automatically every 2-5 minutes. Click to refresh manually">
              <Badge 
                size="lg" 
                variant="light" 
                color="blue" 
                leftSection={<IconActivity size={12} />}
                style={{ cursor: 'help' }}
              >
                Live Data
              </Badge>
            </Tooltip>
          </Group>
        </Paper>

        {/* KPIs Row - Real Data with Enhanced UX */}
        <Grid>
          <Grid.Col span={2.4}>
            <Tooltip 
              label="Percentage of defined session times that have doctors assigned. Higher utilization means better resource efficiency."
              multiline
              w={300}
            >
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <RingProgress
                    size={80}
                    thickness={8}
                    sections={[{ value: kpis.utilization, color: 'blue' }]}
                    label={
                      <Text size="xs" ta="center" fw={600}>
                        {kpis.utilization}%
                      </Text>
                    }
                  />
                  <Group gap={4} align="center">
                    <Text size="sm" fw={500}>Utilization</Text>
                    <IconInfoCircle size={14} color="gray" />
                  </Group>
                  <Text size="xs" c="dimmed" ta="center">
                    Room efficiency
                  </Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
          
          <Grid.Col span={2.4}>
            <Tooltip 
              label="Number of available time slots that are currently unassigned. These represent opportunities for scheduling new assignments."
              multiline
              w={300}
            >
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={700} c="orange">
                    {kpis.vacantSessions}
                  </Text>
                  <Group gap={4} align="center">
                    <Text size="sm" fw={500}>Vacant Sessions</Text>
                    <IconInfoCircle size={14} color="gray" />
                  </Group>
                  <Text size="xs" c="dimmed" ta="center">
                    Available slots
                  </Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
          
          <Grid.Col span={2.4}>
            <Tooltip 
              label="Total hours that rooms are unavailable due to maintenance, cleaning, or administrative blocks. These hours cannot be scheduled."
              multiline
              w={300}
            >
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={700} c="red">
                    {kpis.blockedHours}
                  </Text>
                  <Group gap={4} align="center">
                    <Text size="sm" fw={500}>Blocked Hours</Text>
                    <IconInfoCircle size={14} color="gray" />
                  </Group>
                  <Text size="xs" c="dimmed" ta="center">
                    Unavailable time
                  </Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
          
          <Grid.Col span={2.4}>
            <Tooltip 
              label="Number of scheduling conflicts detected (double bookings, capacity violations, specialty mismatches, etc.). Zero conflicts indicate successful scheduling."
              multiline
              w={300}
            >
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <Text size="xl" fw={700} c={kpis.conflicts > 0 ? 'red' : 'green'}>
                    {kpis.conflicts}
                  </Text>
                  <Group gap={4} align="center">
                    <Text size="sm" fw={500}>Conflicts</Text>
                    <IconInfoCircle size={14} color="gray" />
                  </Group>
                  <Text size="xs" c="dimmed" ta="center">
                    Scheduling issues
                  </Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
          
          <Grid.Col span={2.4}>
            <Tooltip 
              label="Percentage of scheduled assignments that have been finalized and published. Published assignments are visible to doctors and cannot be easily modified."
              multiline
              w={300}
            >
              <Card withBorder p="md" style={{ cursor: 'help' }}>
                <Stack align="center" gap="xs">
                  <Progress
                    value={kpis.publishedCoverage}
                    size="lg"
                    radius="md"
                    color="green"
                  />
                  <Group gap={4} align="center">
                    <Text size="sm" fw={500}>Published Coverage</Text>
                    <IconInfoCircle size={14} color="gray" />
                  </Group>
                  <Text size="xs" c="dimmed" ta="center">
                    Finalized schedules
                  </Text>
                </Stack>
              </Card>
            </Tooltip>
          </Grid.Col>
        </Grid>

        {/* Management Boards - Enhanced with Tooltips */}
        <Grid>
          <Grid.Col span={6}>
            <Paper p="md" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <Text fw={600}>Today's Room States</Text>
                  <Tooltip label="Current status of all rooms: assigned (with doctors), vacant (available), or blocked (unavailable)">
                    <ActionIcon variant="subtle" color="gray" size="sm">
                      <IconHelp size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Text size="xs" c="dimmed">
                  {roomStates?.length || 0} rooms
                </Text>
              </Group>
              {roomStatesError ? (
                <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                  <Text size="sm">Error loading room states</Text>
                </Alert>
              ) : !roomStates || roomStates.length === 0 ? (
                <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
                  <Text size="sm">No room data available</Text>
                </Alert>
              ) : (
                <Stack gap="xs">
                  {roomStates.map((room, index) => (
                    <Tooltip 
                      key={index}
                      label={`Room ${room.room}: ${room.status}${room.doctor ? ` - Assigned to ${room.doctor}` : ''}${room.session ? ` - Session: ${room.session}` : ''}`}
                      multiline
                      w={250}
                    >
                      <Group justify="space-between" p="xs" style={{ 
                        borderRadius: '8px',
                        cursor: 'help',
                        backgroundColor: 'var(--mantine-color-gray-0)',
                        transition: 'background-color 0.2s'
                      }}>
                        <Group gap="sm">
                          <Text size="sm" fw={500}>{room.room}</Text>
                          {room.doctor && (
                            <Text size="xs" c="dimmed">Dr. {room.doctor}</Text>
                          )}
                        </Group>
                        <Badge
                          color={
                            room.status === 'assigned' ? 'green' :
                            room.status === 'vacant' ? 'gray' :
                            room.status === 'blocked' ? 'red' : 'blue'
                          }
                          variant="light"
                          size="sm"
                        >
                          {room.status.toUpperCase()}
                        </Badge>
                      </Group>
                    </Tooltip>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid.Col>
          
          <Grid.Col span={6}>
            <Paper p="md" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <Text fw={600}>Upcoming Blocks</Text>
                  <Tooltip label="Scheduled maintenance, cleaning, or administrative blocks that will make rooms unavailable">
                    <ActionIcon variant="subtle" color="gray" size="sm">
                      <IconHelp size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Text size="xs" c="dimmed">
                  {upcomingBlocks?.length || 0} blocks
                </Text>
              </Group>
              {blocksError ? (
                <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                  <Text size="sm">Error loading blocks</Text>
                </Alert>
              ) : !upcomingBlocks || upcomingBlocks.length === 0 ? (
                <Alert color="green" variant="light" icon={<IconCheck size={16} />}>
                  <Text size="sm">No upcoming blocks scheduled</Text>
                </Alert>
              ) : (
                <Stack gap="xs">
                  {upcomingBlocks.map((block, index) => (
                    <Tooltip 
                      key={index}
                      label={`${block.type} block for Room ${block.room} starting at ${block.startTime}`}
                      multiline
                      w={250}
                    >
                      <Group justify="space-between" p="xs" style={{ 
                        borderRadius: '8px',
                        cursor: 'help',
                        backgroundColor: 'var(--mantine-color-gray-0)',
                        transition: 'background-color 0.2s'
                      }}>
                        <Group gap="sm">
                          <Text size="sm" fw={500}>{block.room}</Text>
                          <Text size="xs" c="dimmed">{block.startTime}</Text>
                        </Group>
                        <Badge color="orange" variant="light" size="sm">
                          {block.type.toUpperCase()}
                        </Badge>
                      </Group>
                    </Tooltip>
                  ))}
                </Stack>
              )}
            </Paper>
          </Grid.Col>
        </Grid>

        {/* Recent Conflicts - Enhanced with Better UX */}
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Text fw={600}>Recent Conflicts</Text>
              <Tooltip label="Scheduling conflicts that need attention: double bookings, capacity violations, specialty mismatches, or other rule violations">
                <ActionIcon variant="subtle" color="gray" size="sm">
                  <IconHelp size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Badge 
              color={conflicts && conflicts.length > 0 ? "red" : "green"} 
              variant="light"
              leftSection={conflicts && conflicts.length > 0 ? <IconAlertTriangle size={12} /> : <IconCheck size={12} />}
            >
              {conflicts?.length || 0} {conflicts && conflicts.length > 0 ? 'Active' : 'Resolved'}
            </Badge>
          </Group>
          {conflictsError ? (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">Error loading conflicts</Text>
            </Alert>
          ) : !conflicts || conflicts.length === 0 ? (
            <Alert color="green" variant="light" icon={<IconCheck size={16} />}>
              <Group gap="sm">
                <Text size="sm" c="green">No conflicts - all assignments successful!</Text>
              </Group>
            </Alert>
          ) : (
            <Stack gap="xs">
              {conflicts.map((conflict) => (
                <Tooltip 
                  key={conflict.id}
                  label={`Conflict in Room ${conflict.room} with Dr. ${conflict.doctor}: ${conflict.issue} at ${conflict.time}`}
                  multiline
                  w={300}
                >
                  <Group 
                    justify="space-between" 
                    p="xs" 
                    style={{ 
                      backgroundColor: 'var(--mantine-color-red-0)',
                      borderRadius: '8px',
                      cursor: 'help',
                      border: '1px solid var(--mantine-color-red-2)'
                    }}
                  >
                    <Group gap="sm">
                      <ThemeIcon color="red" variant="light" size="sm">
                        <IconAlertTriangle size={16} />
                      </ThemeIcon>
                      <Text size="sm" fw={500}>{conflict.room} - {conflict.doctor}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">{conflict.issue} at {conflict.time}</Text>
                  </Group>
                </Tooltip>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Container>
  );
};

export default CoordinatorDashboard;
